/**
 * The merge rules "Import everything" uses to RESTORE a backup (plan 3.2,
 * 2026-10-01). PURE - no signals, no storage, no network - so they can be
 * tested on their own (`smokes/export_everything_restores_a_wiped_browser.ts`
 * drives them end to end).
 *
 * The one rule underneath all of them: a restore never makes old data look
 * new, and never replaces what is already here with something older. A backup
 * restored into a browser that has since paired again must not beat the newer
 * copies OPBridge holds, and a setting this device already chose stays chosen.
 */

import type { RigStateLibrary, RigStateBank, MatrixLibrary } from '@opbuttons/schema';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { matrixAdapter } from '../matrix/matrixRecords.ts';

/** The Matrix, record by record (audit 2026-11-03 E-6): a device or a signal
 *  (its slot and claims) this device lacks is added; one it has is KEPT, since
 *  Matrix records carry no date to say which copy is newer. The records are the
 *  ones the Matrix sync uses (`matrixRecords.ts`), so a restore can only ADD
 *  documents to OPBridge, never delete one. This used to be a wholesale
 *  replace, against Settings' own promise ("anything already here is kept
 *  unless the file's copy is newer"), and the record sync then deleted every
 *  record the backup lacked, and overwrote newer ones, on every PC.
 *  `next` is null when nothing changes. */
export function restoreMatrixRecords(
  local: MatrixLibrary,
  incoming: MatrixLibrary | undefined,
): { next: MatrixLibrary | null; added: number } {
  if (!incoming || !Array.isArray(incoming.devices) || !Array.isArray(incoming.slots)) return { next: null, added: 0 };
  const have = matrixAdapter.split(local);
  const puts = new Map<string, unknown>();
  for (const [id, rec] of matrixAdapter.split(incoming)) {
    if (!have.has(id) && !matrixAdapter.problem(id, rec)) puts.set(id, rec);
  }
  if (puts.size === 0) return { next: null, added: 0 };
  const next = matrixAdapter.apply(local, puts, new Set());
  const added = [...puts.keys()].filter((id) => matrixAdapter.split(next).has(id)).length;
  return { next: added > 0 ? next : null, added };
}

function stampMs(iso: string | undefined): number {
  const n = Date.parse(iso ?? '');
  return Number.isFinite(n) ? n : 0;
}

/** Rig State banks by id: a bank this device lacks is added; one it has is
 *  replaced only by a NEWER copy (`modified`); a tie keeps the local one.
 *  `next` is null when nothing changes. */
export function mergeRigStateBanks(
  local: RigStateLibrary,
  incoming: RigStateLibrary | undefined,
): { next: RigStateLibrary | null; added: number; updated: number } {
  const banks = Array.isArray(incoming?.banks) ? incoming!.banks : [];
  let added = 0;
  let updated = 0;
  const byId = new Map(local.banks.map((b, i) => [b.id, i]));
  const out: RigStateBank[] = [...local.banks];
  for (const b of banks) {
    if (!b || typeof b.id !== 'string' || !Array.isArray(b.looks)) continue;
    const i = byId.get(b.id);
    if (i === undefined) { out.push(b); byId.set(b.id, out.length - 1); added++; continue; }
    if (stampMs(b.modified) > stampMs(out[i]!.modified)) { out[i] = b; updated++; }
  }
  return { next: added + updated > 0 ? { ...local, banks: out } : null, added, updated };
}

function sameUrl(a: string, b: string): boolean {
  const n = (s: string) => s.trim().replace(/\/+$/, '').toLowerCase();
  return n(a) === n(b);
}

/**
 * Which bridge pairings to add back. A row is skipped when this device already
 * has it - same row id, same OPBridge (`nodeId`), or same address - so a
 * restore never doubles a pairing or overwrites a newer one. Restored rows
 * keep their row id: controls aim by the OPBridge's node id now
 * (`layout/bridgeAffinityMigration.ts`), but a pedal still carries the row id
 * beside its node id, and an unchanged row is the least surprising restore.
 *
 * Primary: on a device with no pairings the backup's primary is kept; on one
 * that has its own, that device's primary stays and the restored rows join
 * as peers.
 */
export function planBridgeRestore(
  local: readonly BridgeConfig[],
  incoming: readonly unknown[] | undefined,
): { add: BridgeConfig[]; skipped: number } {
  const add: BridgeConfig[] = [];
  let skipped = 0;
  const keepPrimary = local.length === 0;
  for (const raw of incoming ?? []) {
    const r = raw as Partial<BridgeConfig> | null;
    if (!r || typeof r.id !== 'string' || typeof r.baseUrl !== 'string' || !r.baseUrl) { skipped++; continue; }
    const all = [...local, ...add];
    const dup = all.some((x) => x.id === r.id
      || (!!r.nodeId && x.nodeId === r.nodeId)
      || sameUrl(x.baseUrl, r.baseUrl!));
    if (dup) { skipped++; continue; }
    add.push({ ...(r as BridgeConfig), is_primary: keepPrimary ? r.is_primary === true : false });
  }
  return { add, skipped };
}

/**
 * Per-device preferences a backup carries. Restored ONLY where this device
 * has not chosen its own (the key is absent), because they are per-device
 * by design - the iPad on stage and the laptop at the desk may want different
 * skins and offsets. A wiped browser has chosen nothing, so it gets them all.
 *
 * Deliberately NOT here (device state, never travels): sync bookkeeping,
 * deletion records, caches, latches, first-run flags, kill switches, this
 * install's device id and its sign-in session.
 */
export const RESTORABLE_SETTING_KEYS: readonly string[] = [
  'opbuttons.skin.v1',
  'opbuttons.barPosition.v1',
  'opbuttons.editTriggers.v1',
  'opbuttons.transport.offset_ms',
  'opbuttons.screenDim.v2',
  'opbuttons.preflight.thresholds.v1',
  'opbuttons.helix.setlistNames.v1',
];

/** The allow-listed settings to write: present in the backup, absent here. */
export function settingsToRestore(
  incoming: Record<string, unknown> | undefined,
  isSetHere: (key: string) => boolean,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!incoming || typeof incoming !== 'object') return out;
  for (const key of RESTORABLE_SETTING_KEYS) {
    const v = incoming[key];
    if (typeof v !== 'string') continue;
    if (isSetHere(key)) continue;
    out[key] = v;
  }
  return out;
}
